import { createTheme, Theme } from '@mui/material/styles';
import { designTokens } from './designTokens';

declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      blue: {
        primary: string;
        light: string;
        dark: string;
      };
      yellow: {
        lightMax: string;
      };
      pink: {
        lightMax: string;
      };
    };
  }

  interface PaletteOptions {
    brand?: {
      blue?: {
        primary?: string;
        light?: string;
        dark?: string;
      };
      yellow?: {
        lightMax?: string;
      };
      pink?: {
        lightMax?: string;
      };
    };
  }
}

export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const isLight = mode === 'light';
  
  return createTheme({
    palette: {
      mode,
      // Primary uses the real Rash ID brand blue
      primary: {
        main: designTokens.colors.brand.blue.primary, // #91abdf
        dark: designTokens.colors.brand.blue.dark,    // #839ed4
        light: designTokens.colors.brand.blue.light,  // #9bb4e5
        contrastText: designTokens.colors.text.quaternary, // #ffffff
      },
      secondary: {
        main: designTokens.colors.brand.pink.lightMax, // #faf1f7
        light: designTokens.colors.brand.yellow.lightMax, // #f5f3e9
        contrastText: designTokens.colors.text.primary,
      },
      error: {
        main: designTokens.colors.semantic.error, // #df4646
        light: '#fee2e2',
        dark: '#dc2626',
      },
      warning: {
        main: designTokens.colors.semantic.warning, // #fdb022
        light: '#fef3c7',
        dark: '#d97706',
      },
      success: {
        main: '#10b981', // Keep a nice green for success
        light: '#d1fae5',
        dark: '#059669',
      },
      info: {
        main: designTokens.colors.brand.blue.primary,
        light: designTokens.colors.brand.blue.light,
        dark: designTokens.colors.brand.blue.dark,
      },
      background: {
        default: isLight 
          ? 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 25%, #F1F5F9 50%, #E6E3F7 75%, #F3E8FF 100%)'
          : designTokens.colors.background.primary,
        paper: isLight 
          ? designTokens.colors.background.octonary 
          : designTokens.colors.background.primary,
      },
      text: {
        primary: isLight 
          ? designTokens.colors.text.primary 
          : designTokens.colors.text.quaternary,
        secondary: isLight 
          ? designTokens.colors.text.secondary 
          : designTokens.colors.text.tertiary,
        disabled: designTokens.colors.text.tertiary,
      },
      divider: isLight 
        ? designTokens.colors.border.secondary 
        : designTokens.colors.shades[24],
      
      // Real Rash ID brand colors accessible via theme.palette.brand
      brand: designTokens.colors.brand,
    },
    typography: {
      fontFamily: designTokens.typography.fontFamily,
      h1: {
        fontSize: designTokens.typography.fontSize.xl,
        fontWeight: designTokens.typography.fontWeight.semiBold,
        lineHeight: designTokens.typography.lineHeight.lg / designTokens.typography.fontSize.xl,
      },
      h2: {
        fontSize: 32,
        fontWeight: designTokens.typography.fontWeight.semiBold,
        lineHeight: 1.25,
      },
      h3: {
        fontSize: 28,
        fontWeight: designTokens.typography.fontWeight.semiBold,
        lineHeight: 1.25,
      },
      h4: {
        fontSize: 24,
        fontWeight: designTokens.typography.fontWeight.semiBold,
        lineHeight: 1.25,
      },
      h5: {
        fontSize: 20,
        fontWeight: designTokens.typography.fontWeight.semiBold,
        lineHeight: 1.25,
      },
      h6: {
        fontSize: designTokens.typography.fontSize.md,
        fontWeight: designTokens.typography.fontWeight.semiBold,
        lineHeight: 1.25,
      },
      body1: {
        fontSize: designTokens.typography.fontSize.md,
        lineHeight: 1.5,
      },
      body2: {
        fontSize: designTokens.typography.fontSize.sm,
        lineHeight: designTokens.typography.lineHeight.sm / designTokens.typography.fontSize.sm,
      },
      button: {
        fontSize: designTokens.typography.fontSize.sm,
        fontWeight: designTokens.typography.fontWeight.medium,
        textTransform: 'none',
      },
    },
    spacing: 4,
    shape: {
      borderRadius: designTokens.borderRadius.md,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.md,
            textTransform: 'none',
            fontWeight: designTokens.typography.fontWeight.medium,
            padding: `${designTokens.spacing.sm}px ${designTokens.spacing.lg}px`,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.lg,
            boxShadow: isLight 
              ? '0 4px 20px rgba(0,0,0,0.08)'
              : '0 4px 8px rgba(0,0,0,0.3)',
            border: isLight ? '1px solid rgba(0,0,0,0.04)' : 'none',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: isLight
                ? '0 8px 30px rgba(0,0,0,0.12)'
                : '0 6px 12px rgba(0,0,0,0.4)',
              transform: 'translateY(-2px)',
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.lg,
            boxShadow: isLight 
              ? '0 4px 20px rgba(0,0,0,0.08)'
              : '0 4px 8px rgba(0,0,0,0.3)',
            border: isLight ? '1px solid rgba(0,0,0,0.04)' : 'none',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: designTokens.borderRadius.md,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: designTokens.borderRadius.max,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: isLight 
              ? 'rgba(255, 255, 255, 0.95)'
              : 'rgba(26, 32, 44, 0.95)',
            backdropFilter: 'blur(20px)',
            color: theme.palette.text.primary,
            boxShadow: isLight 
              ? '0 8px 32px rgba(0,0,0,0.08)'
              : '0 1px 3px rgba(255,255,255,0.12)',
            border: `1px solid ${theme.palette.divider}`,
          }),
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: ({ theme }) => ({
            backgroundColor: isLight 
              ? 'rgba(255, 255, 255, 0.98)'
              : 'rgba(26, 32, 44, 0.98)',
            backdropFilter: 'blur(10px)',
            borderRight: `1px solid ${theme.palette.divider}`,
            boxShadow: isLight 
              ? '4px 0 24px rgba(0,0,0,0.04)'
              : '4px 0 24px rgba(0,0,0,0.25)',
          }),
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: designTokens.borderRadius.md,
            margin: `0 ${designTokens.spacing.xs}px`,
            '&:hover': {
              backgroundColor: isLight 
                ? 'rgba(145, 171, 223, 0.1)'
                : 'rgba(145, 171, 223, 0.15)',
            },
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.contrastText,
              },
            },
          }),
        },
      },
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: designTokens.spacing.lg,
            paddingRight: designTokens.spacing.lg,
          },
        },
      },
    },
  });
};